import * as React from 'react';
import { approvalsHooks } from '../network/endpoints.js';
import type { ApprovalItem, ApprovalType } from '../types.js';
import { getSession } from '../../../lib/session.js';
import { Money } from '../../../components/ui/Money.js';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale.js';
import { Button } from '../../../components/ui/Button.js';
import { Input } from '../../../components/ui/FormField.js';
import { Decimal } from 'decimal.js';
import { generalTransactionsHooks } from '../../accounting/network/general-transactions.api.js';

interface ApprovalDetailDrawerProps {
 isOpen: boolean;
 onClose: () => void;
 itemId: string | null;
 itemType: ApprovalType | null;
 onSuccess: () => void;
}

export function ApprovalDetailDrawer({
 isOpen,
 onClose,
 itemId,
 itemType,
 onSuccess,
}: ApprovalDetailDrawerProps) {
 const session = getSession();
 const currentUserId = session?.user?.id;

 const [registrarRef, setRegistrarRef] = React.useState('');
 const [rejectReason, setRejectReason] = React.useState('');
 const [showRejectForm, setShowRejectForm] = React.useState(false);
 const [showApproveForm, setShowApproveForm] = React.useState(false);
 const [approveError, setApproveError] = React.useState<string | undefined>();
 const [rejectError, setRejectError] = React.useState<string | undefined>();

 const { data: item, isLoading, error } = approvalsHooks.useDetail(
 itemType!,
 itemId!,
 );

 const generalTransactionId = itemType === 'GENERAL_TRANSACTION_POST' ? item?.payload?.transactionId ?? null : null;
 const { data: generalTransaction } = generalTransactionsHooks.useDetail(generalTransactionId);

 const approveMutation = approvalsHooks.useApprove();
 const rejectMutation = approvalsHooks.useReject();
 const cancelMutation = approvalsHooks.useCancel();

 React.useEffect(() => {
 if (isOpen) {
 setRegistrarRef('');
 setRejectReason('');
 setShowRejectForm(false);
 setShowApproveForm(false);
 setApproveError(undefined);
 setRejectError(undefined);
 }
 }, [isOpen, itemId, itemType]);

 if (!isOpen) return null;

 const handleApprove = () => {
 if (!itemType || !itemId) return;
 if (itemType === 'SCHEME_REGISTRATION' && !registrarRef.trim()) {
 setApproveError('Registrar reference is required.');
 return;
 }

 approveMutation.mutate(
 {
 type: itemType,
 id: itemId,
 registrarRef: itemType === 'SCHEME_REGISTRATION' ? registrarRef : undefined,
 },
 {
 onSuccess: () => {
 onSuccess();
 onClose();
 },
 onError: (err: unknown) => {
 setApproveError(`Approval failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
 },
 },
 );
 };

 const handleReject = () => {
 if (!itemType || !itemId) return;
 if (rejectReason.trim().length < 20) {
 setRejectError('Rejection reason must be at least 20 characters.');
 return;
 }

 rejectMutation.mutate(
 {
 type: itemType,
 id: itemId,
 reason: rejectReason,
 },
 {
 onSuccess: () => {
 onSuccess();
 onClose();
 },
 onError: (err: unknown) => {
 setRejectError(`Rejection failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
 },
 },
 );
 };

 const handleCancel = () => {
 if (!itemId) return;
 cancelMutation.mutate(
 {
 type: itemType ?? undefined,
 id: itemId,
 },
 {
 onSuccess: () => {
 onSuccess();
 onClose();
 },
 onError: (err: unknown) => {
 setRejectError(`Cancellation failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
 },
 },
 );
 };

 const isMaker = item && currentUserId === item.makerId;

 return (
 <div className="fixed inset-0 z-modal overflow-hidden" aria-labelledby="slide-over-title" role="dialog" aria-modal="true">
 <div className="absolute inset-0 overflow-hidden">
 {/* Backdrop */}
 <div 
 onClick={onClose}
 className="absolute inset-0 bg-black/50 transition-opacity "
 ></div>

 <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
 <div className="pointer-events-auto w-screen max-w-md transform bg-surface-1 shadow-2xl transition-all border-l border-border flex flex-col h-full">
 {/* Drawer Header */}
 <div className="px-6 py-5 bg-surface-2 border-b border-border flex items-center justify-between">
 <h2 className="text-lg font-bold text-text-primary" id="slide-over-title">
 Approval Details
 </h2>
 <Button
 variant="ghost" 
 onClick={onClose}
 className="text-text-secondary hover:text-text-primary p-1 rounded-full hover:bg-surface-3 transition-colors"
 >
 <span className="sr-only">Close panel</span>
 <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
 </svg>
 </Button>
 </div>

 {/* Drawer Body */}
 <div className="flex-1 overflow-y-auto p-6 space-y-6">
 {isLoading ? (
 <div className="flex items-center justify-center h-48 text-text-muted">
 Loading details...
 </div>
 ) : error ? (
 <div className="p-4 bg-danger-bg text-danger-text rounded-md text-sm">
 Failed to load approval details. Please refresh the page and try again.
 </div>
 ) : item ? (
 <>
 {/* Category and Summary */}
 <div>
 <span className="inline-flex items-center rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-700">
 {item.type.replace(/_/g, ' ')}
 </span>
 <h3 className="mt-2 text-xl font-bold text-text-primary">
 {item.summary}
 </h3>
 </div>

 {/* Creator Info */}
 <div className="bg-surface-2 p-4 rounded-lg border border-border space-y-2">
 <div className="flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Submitted By:</span>
 <span className="text-text-primary font-bold">{item.makerEmail}</span>
 </div>
 <div className="flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Submitted At:</span>
 <span className="text-text-primary font-semibold">
 {new Date(item.createdAt).toLocaleString()}
 </span>
 </div>
 </div>

 {/* Detailed Fields based on operation type */}
 <div className="space-y-4">
 <h4 className="text-sm font-bold uppercase tracking-wider text-text-secondary">
 Attributes
 </h4>

 <div className="border border-border rounded-lg overflow-hidden divide-y divide-border">
 {item.amount !== null && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Amount:</span>
 <span className="text-text-primary font-bold">
 <Money paise={item.amount} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
 </span>
 </div>
 )}

 {/* SCHEME_REGISTRATION */}
 {item.type === 'SCHEME_REGISTRATION' && (
 <>
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Branch ID:</span>
 <span className="text-text-primary font-semibold">{item.payload?.branchId}</span>
 </div>
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Subscriber Count:</span>
 <span className="text-text-primary font-semibold">{item.payload?.subscriberCount}</span>
 </div>
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Duration:</span>
 <span className="text-text-primary font-semibold">{item.payload?.durationCycles} cycles</span>
 </div>
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Frequency:</span>
 <span className="text-text-primary font-semibold">{item.payload?.cycleFrequency}</span>
 </div>
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Commission Rate:</span>
 <span className="text-text-primary font-semibold">{new Decimal(item.payload?.commissionRate || '0' || 0).toNumber() * 100}%</span>
 </div>
 </>
 )}

 {/* SUBSCRIBER_TRANSFER */}
 {item.type === 'SUBSCRIBER_TRANSFER' && (
 <>
 {item.payload?.schemeName && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Scheme:</span>
 <span className="text-text-primary font-semibold">{item.payload.schemeName}</span>
 </div>
 )}
 {item.payload?.fromCustomerName && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">From Member:</span>
 <span className="text-text-primary font-semibold">{item.payload.fromCustomerName}</span>
 </div>
 )}
 {item.payload?.toCustomerName && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">To Member:</span>
 <span className="text-text-primary font-semibold">{item.payload.toCustomerName}</span>
 </div>
 )}
 {item.payload?.ticketNumbers && item.payload.ticketNumbers.length > 0 && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Ticket Numbers:</span>
 <span className="text-text-primary font-semibold">{item.payload.ticketNumbers.join(', ')}</span>
 </div>
 )}
 {item.payload?.valuePaise !== undefined && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Transfer Value:</span>
 <span className="text-text-primary font-bold">
 <Money paise={item.payload.valuePaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
 </span>
 </div>
 )}
 {item.payload?.remarks && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Remarks:</span>
 <span className="text-text-primary font-semibold">{item.payload.remarks}</span>
 </div>
 )}
 </>
 )}

 {/* RECEIPT_VOUCHER_CREATE */}
 {item.type === 'RECEIPT_VOUCHER_CREATE' && (
 <>
 {item.payload?.category && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Category:</span>
 <span className="text-text-primary font-semibold">{item.payload.category}</span>
 </div>
 )}
 {item.payload?.depositorName && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Depositor Name:</span>
 <span className="text-text-primary font-semibold">{item.payload.depositorName}</span>
 </div>
 )}
 {item.payload?.amountPaise !== undefined && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Voucher Amount:</span>
 <span className="text-text-primary font-bold">
 <Money paise={item.payload.amountPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
 </span>
 </div>
 )}
 </>
 )}

 {/* SETTLEMENT_DISBURSEMENT */}
 {item.type === 'SETTLEMENT_DISBURSEMENT' && (
 <>
 {item.payload?.settlementNumber && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Settlement #:</span>
 <span className="text-text-primary font-semibold font-mono">{item.payload.settlementNumber}</span>
 </div>
 )}
 {item.payload?.settlementType && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Settlement Type:</span>
 <span className="text-text-primary font-semibold">{item.payload.settlementType}</span>
 </div>
 )}
 {item.payload?.grossAmountPaise !== undefined && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Gross Amount:</span>
 <span className="text-text-primary font-bold">
 <Money paise={item.payload.grossAmountPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
 </span>
 </div>
 )}
 {item.payload?.deductionsPaise !== undefined && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Deductions:</span>
 <span className="text-text-primary font-semibold text-danger-500">
 <Money paise={item.payload.deductionsPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
 </span>
 </div>
 )}
 {item.payload?.netPayoutAmountPaise !== undefined && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Net Payout:</span>
 <span className="text-text-primary font-bold">
 <Money paise={item.payload.netPayoutAmountPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
 </span>
 </div>
 )}
 {item.payload?.payoutMethod && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Payout Method:</span>
 <span className="text-text-primary font-semibold">{item.payload.payoutMethod}</span>
 </div>
 )}
 {item.payload?.beneficiaryBankDetails && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Beneficiary Bank:</span>
 <span className="text-text-primary font-semibold">
 {item.payload.beneficiaryBankDetails.bankName ?? 'Bank'} (A/C: {item.payload.beneficiaryBankDetails.accountNumber ? `••••${String(item.payload.beneficiaryBankDetails.accountNumber).slice(-4)}` : '—'}, IFSC: {item.payload.beneficiaryBankDetails.ifscCode ?? '—'})
 </span>
 </div>
 )}
 </>
 )}

 {/* RECEIPT_REVERSAL */}
 {item.type === 'RECEIPT_REVERSAL' && (
 <>
 {item.payload?.receiptId && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Receipt ID:</span>
 <span className="text-text-primary font-semibold font-mono">{item.payload.receiptId}</span>
 </div>
 )}
 {item.payload?.reversalReason && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Reversal Reason:</span>
 <span className="text-text-primary font-semibold">{item.payload.reversalReason}</span>
 </div>
 )}
 {item.payload?.lines && (
 <div className="p-3 bg-surface-1 text-sm space-y-2">
 <span className="text-text-secondary font-medium block">Double-Entry Lines:</span>
 <div className="space-y-1.5 border border-border p-2 rounded bg-surface-2 max-h-48 overflow-y-auto">
 {(item.payload.lines || []).map((line: { accountId: string; drAmount: number; crAmount: number }, index: number) => (
 <div key={index} className="flex justify-between text-xs font-mono">
 <span className="truncate text-text-primary max-w-[150px]">{line.accountId}</span>
 <div className="space-x-2">
 {line.drAmount > 0 && (
 <span className="text-success-500 font-bold">DR: <Money paise={line.drAmount} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /></span>
 )}
 {line.crAmount > 0 && (
 <span className="text-danger-500 font-bold">CR: <Money paise={line.crAmount} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /></span>
 )}
 </div>
 </div>
 ))}
 </div>
 </div>
 )}
 </>
 )}

 {/* PAYOUT_REVERSED */}
 {item.type === 'PAYOUT_REVERSED' && (
 <>
 {item.payload?.reversalReason && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Reversal Reason:</span>
 <span className="text-text-primary font-semibold">{item.payload.reversalReason}</span>
 </div>
 )}
 </>
 )}

 {/* AUCTION_RESULT_CORRECTED */}
 {item.type === 'AUCTION_RESULT_CORRECTED' && (
 <>
 {item.payload?.reason && (
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Correction Reason:</span>
 <span className="text-text-primary font-semibold">{item.payload.reason}</span>
 </div>
 )}
 </>
 )}

 {/* EXIT_REFUND */}
 {item.type === 'EXIT_REFUND' && (
 <>
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Exit Type:</span>
 <span className="text-text-primary font-semibold">{item.payload?.exitType}</span>
 </div>
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Foreman Deduction:</span>
 <span className="text-text-primary font-semibold">
 <Money paise={item.payload?.foremanDeduction || 0} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
 </span>
 </div>
 </>
 )}

 {/* BANK_ACCOUNT_UPDATE */}
 {item.type === 'BANK_ACCOUNT_UPDATE' && (
 <>
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Bank Name:</span>
 <span className="text-text-primary font-semibold">{item.payload?.bankName ?? '—'}</span>
 </div>
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">IFSC:</span>
 <span className="text-text-primary font-semibold">{item.payload?.bankIfsc ?? '—'}</span>
 </div>
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Account Number:</span>
 <span className="text-text-primary font-semibold">{item.payload?.bankAccountNumber ?? '—'}</span>
 </div>
 </>
 )}

 {/* CONFIG_CHANGE */}
 {item.type === 'CONFIG_CHANGE' && (
 <>
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Config Key:</span>
 <span className="text-text-primary font-semibold font-mono">{item.payload?.key ?? '—'}</span>
 </div>
 <div className="p-3 bg-surface-1 flex justify-between text-sm">
 <span className="text-text-secondary font-medium">Proposed Value:</span>
 <span className="text-text-primary font-semibold">{String(item.payload?.value ?? '—')}</span>
 </div>
 </>
 )}

 {/* GENERAL_TRANSACTION_POST */}
 {item.type === 'GENERAL_TRANSACTION_POST' && (
 <div className="p-3 bg-surface-1 text-sm space-y-2">
 {generalTransaction ? (
 <>
 <div className="flex justify-between text-sm mb-2">
 <span className="text-text-secondary font-medium">Type:</span>
 <span className="text-text-primary font-semibold">{generalTransaction.type}</span>
 </div>
 <div className="flex justify-between text-sm mb-2">
 <span className="text-text-secondary font-medium">Particulars:</span>
 <span className="text-text-primary font-semibold text-right max-w-[200px] truncate">{generalTransaction.particulars}</span>
 </div>
 <span className="text-text-secondary font-medium block">Lines:</span>
 <div className="space-y-1.5 border border-border p-2 rounded bg-surface-2 max-h-48 overflow-y-auto">
 {generalTransaction.lines.map((line) => (
 <div key={line.id} className="flex justify-between text-xs font-mono">
 <span className="truncate text-text-primary max-w-[150px]">{line.accountName}</span>
 <div className="space-x-2">
 {line.direction === 'DEBIT' && (
 <span className="text-success-500 font-bold">DR: <Money paise={line.amountPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /></span>
 )}
 {line.direction === 'CREDIT' && (
 <span className="text-danger-500 font-bold">CR: <Money paise={line.amountPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /></span>
 )}
 </div>
 </div>
 ))}
 </div>
 </>
 ) : (
 <span className="text-text-muted">Loading transaction detail…</span>
 )}
 </div>
 )}
 </div>
 </div>

 {/* Approve Sub-Form / Confirmation */}
 {showApproveForm && (
 <div className="bg-success-bg/10 border border-success-text/20 p-4 rounded-lg space-y-4">
 <h4 className="text-sm font-bold text-success-text">Approve Request</h4>
 {item.type === 'SCHEME_REGISTRATION' && (
 <div className="space-y-1.5">
 <label className="text-xs font-bold text-text-primary block">
 Registrar Reference <span className="text-danger-500">*</span>
 </label>
 <Input
 type="text"
 value={registrarRef}
 onChange={(e) => { setRegistrarRef(e.target.value); setApproveError(undefined); }}
 placeholder="Enter Govt Registrar Ref"
 className="bg-surface-1"
 error={!!approveError}
 />
 </div>
 )}
 {approveError && <p className="text-xs text-danger-text">{approveError}</p>}
 <div className="flex space-x-3">
 <Button
 variant="primary"
 onClick={handleApprove}
 loading={approveMutation.isPending}
 className="flex-1 bg-success-500 hover:bg-success-600 border-none text-white text-sm font-bold py-2 px-4 rounded transition-colors"
 >
 Confirm Approval
 </Button>
 <Button
 variant="secondary"
 onClick={() => setShowApproveForm(false)}
 className="bg-surface-3 hover:bg-surface-4 text-text-primary text-sm font-bold py-2 px-4 rounded transition-colors"
 >
 Cancel
 </Button>
 </div>
 </div>
 )}

 {/* Reject Sub-Form */}
 {showRejectForm && (
 <div className="bg-danger-bg/10 border border-danger-text/20 p-4 rounded-lg space-y-4">
 <h4 className="text-sm font-bold text-danger-text">Reject Request</h4>
 <div className="space-y-1.5">
 <label className="text-xs font-bold text-text-primary block">
 Rejection Reason <span className="text-danger-500">*</span>
 </label>
 <textarea
 value={rejectReason}
 onChange={(e) => { setRejectReason(e.target.value); setRejectError(undefined); }}
 placeholder="Provide a detailed reason for rejection..."
 rows={3}
 className={`w-full px-3 py-2 text-sm bg-surface-1 border rounded focus:outline-none focus:ring-1 text-text-primary font-medium ${rejectError ? 'border-danger-500 focus:ring-danger-500' : 'border-border focus:ring-danger-500'}`}
 />
 <div className="flex justify-between text-xs font-semibold">
 <span className={rejectReason.length < 20 ? 'text-danger-500' : 'text-success-500'}>
 {rejectReason.length}/20 characters min
 </span>
 </div>
 </div>
 {rejectError && <p className="text-xs text-danger-text">{rejectError}</p>}
 <div className="flex space-x-3">
 <Button
 variant="danger"
 onClick={handleReject}
 disabled={rejectReason.length < 20 || rejectMutation.isPending}
 loading={rejectMutation.isPending}
 className="flex-1"
 >
 Confirm Rejection
 </Button>
 <Button
 variant="secondary"
 onClick={() => setShowRejectForm(false)}
 >
 Cancel
 </Button>
 </div>
 </div>
 )}

 {/* Action Buttons */}
 {!showRejectForm && !showApproveForm && (
 <div className="pt-4 border-t border-border flex space-x-4">
 {isMaker ? (
 <Button
 variant="danger"
 onClick={handleCancel}
 loading={cancelMutation.isPending}
 className="w-full bg-danger-600 hover:bg-danger-700 text-white"
 >
 Cancel My Request
 </Button>
 ) : (
 <>
 <div className="relative flex-1 group">
 <Button
 variant="primary"
 onClick={() => setShowApproveForm(true)}
 className="w-full bg-success-500 hover:bg-success-600 border-none flex-1"
 >
 Approve
 </Button>
 </div>

 <Button
 variant="danger"
 onClick={() => setShowRejectForm(true)}
 className="flex-1"
 >
 Reject
 </Button>
 </>
 )}
 </div>
 )}
 </>
 ) : null}
 </div>
 </div>
 </div>
 </div>
 </div>
 );
}
